import type { SortingState } from '@tanstack/react-table';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { ArtistSearchBar } from '~/modules/artist-roster/components/ArtistSearchBar/ArtistSearchBar';
import { FilterButton } from '~/modules/artist-roster/components/FilterButton/FilterButton';
import { TrackListHeader } from '~/modules/artist-roster/pages/artist-details/pages/artist-tracks/components/TrackList/TrackListHeader';
import { TrackAppliedFilterList } from '~/modules/artist-roster/pages/artist-details/pages/artist-tracks/components/TracksFilter/TrackAppliedFilter/TrackAppliedFilterList';
import type { TracksFilters } from '~/modules/artist-roster/pages/artist-details/utils/types';
import type {
  ArtistDetails,
  FiltersMetadata,
} from '~/modules/artist-roster/types';

interface TracksHeaderProps {
  setIsFilterPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  sortingState: SortingState;
  filters: TracksFilters;
  updateFilters: (newFilters: Partial<TracksFilters>) => void;
  handleApplyFilters: (appliedFilters: TracksFilters) => void;
}

/**
 * This component is wrapped in React.memo() to prevent unnecessary re-renders with
 * the ScrollCollapse logic. See https://github.com/wmgtech/wmg2-one-app/pull/1747
 */
export const TracksHeader: React.FC<TracksHeaderProps> = memo(
  ({
    setIsFilterPanelOpen,
    sortingState,
    filters,
    updateFilters,
    handleApplyFilters,
  }) => {
    const { t } = useTranslation('roster');
    const { searchText, releaseType, role } = filters;

    const updateFiltersPersist = useCallback(
      (newFilters: Partial<TracksFilters>) => {
        updateFilters(newFilters);
      },
      [updateFilters],
    );

    const { artistData, scrollUpCallback } = useOutletContext<{
      artistData: ArtistDetails;
      scrollUpCallback: () => void;
    }>();

    const filtersMetadata: FiltersMetadata = useMemo(() => {
      return {
        areFiltersApplied: Boolean(filters.releaseType || filters.role),
        filterCount: (filters.releaseType ? 1 : 0) + (filters.role ? 1 : 0),
      };
    }, [filters]);

    const handleShowFilters = useCallback(
      (isOpen: boolean) => {
        setIsFilterPanelOpen(isOpen);
      },
      [setIsFilterPanelOpen],
    );

    const handleSubmitSearch = useCallback(
      (searchValue: string) => {
        updateFiltersPersist({ searchText: searchValue });
      },
      [updateFiltersPersist],
    );

    const handleClearFilters = useCallback(() => {
      handleApplyFilters({
        searchText: undefined,
        releaseType: undefined,
        role: undefined,
      });
    }, [handleApplyFilters]);

    const handleSearchInputClear = useCallback(() => {
      updateFiltersPersist({ searchText: undefined });
    }, [updateFiltersPersist]);

    const handleRemoveReleaseTypeFilter = useCallback(() => {
      updateFiltersPersist({
        releaseType: undefined,
      });
    }, [updateFiltersPersist]);

    const handleRemoveRoleFilter = useCallback(() => {
      updateFiltersPersist({
        role: undefined,
      });
    }, [updateFiltersPersist]);

    return (
      <>
        <div className="mb-md flex gap-md" role="search">
          <ArtistSearchBar
            initialSearchText={searchText}
            placeholder={t('filters.searchForTrack')}
            onSubmit={handleSubmitSearch}
            onClear={handleSearchInputClear}
          />
          <FilterButton
            filtersMetadata={filtersMetadata}
            onShowFilters={handleShowFilters}
          />
        </div>
        {releaseType || role ? (
          <TrackAppliedFilterList
            releaseType={releaseType}
            role={role}
            onRemoveReleaseType={handleRemoveReleaseTypeFilter}
            onRemoveRole={handleRemoveRoleFilter}
            onClear={handleClearFilters}
          />
        ) : null}
        <TrackListHeader
          uaid={artistData.uaid}
          artistData={artistData}
          columnSort={sortingState[0]}
          scrollUpCallback={scrollUpCallback}
          role={role}
        />
      </>
    );
  },
);

TracksHeader.displayName = 'TracksHeader';
